import { useAnalytics } from "@codat/analytics";
import {
  ModalBody,
  RadioButton,
  SquareNewWindowIcon,
  TextLink,
  Typography,
} from "@codat/orchard-ui";
import classNames from "classnames";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import Button from "components/Button";
import Radio from "components/Radio";

import styles from "./Onboarding.module.css";

interface HaveExistingIntegrationsModalProps {
  onContinue: () => void;
  onSkip: () => void;
}

export const HaveExistingIntegrationsModal = ({
  onContinue,
  onSkip,
}: HaveExistingIntegrationsModalProps) => {
  const { register, handleSubmit, getValues, watch } = useFormContext();
  const { sendEvent } = useAnalytics();

  const hasAnIntegration = watch("hasAnIntegration");
  const migrateTokens = watch("migrateTokens");

  return (
    <ModalBody
      size="medium"
      title=""
      className={styles.modal}
      data-testid="already-have-a-solution-modal"
    >
      <div className={styles.modalContent}>
        <Typography as="h1" variant="heading_xl" className={styles.modalTitle}>
          Have existing<span> integrations?</span>
        </Typography>
        <Typography
          as="h2"
          variant="body_sm"
          className={classNames(
            styles.modalSubtitle,
            styles.haveASolutionSubtitle
          )}
        >
          Moving live integrations to Codat is easy. We do all the hard work and
          your users stay connected without doing anything.
        </Typography>
        <TextLink
          href="https://docs.codat.io/get-started/migration"
          data-testid="migration-documentation-link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => sendEvent("Migration Documentation Link - Click")}
          className={styles.documentationLink}
        >
          Read our migration documentation
          <SquareNewWindowIcon />
        </TextLink>
        <form className={styles.form} onSubmit={handleSubmit(onContinue)}>
          <div role="radiogroup">
            <Typography as="p" variant="body_md">
              Have you already built an integration straight to a data provider?
            </Typography>
            <Controller
              name="hasAnIntegration"
              render={({ field }) => {
                return (
                  <RadioButton
                    data-testid="has-integration-yes"
                    id="yes"
                    label="Yes, I am already accessing customer data via a direct integration"
                    value="yes"
                    onChange={(val) => field.onChange(val)}
                    ref={field.ref}
                    onFocus={() =>
                      sendEvent("Built An Integration Radio Button - Focus", {
                        value: true,
                      })
                    }
                  />
                );
              }}
            />
            <Controller
              name="hasAnIntegration"
              render={({ field }) => {
                return (
                  <RadioButton
                    data-testid="has-integration-no"
                    id="no"
                    label="No"
                    defaultChecked
                    onChange={(val) => field.onChange(val)}
                    value="no"
                    ref={field.ref}
                    autoFocus
                    onFocus={() =>
                      sendEvent("Built An Integration Radio Button - Focus", {
                        value: false,
                      })
                    }
                  />
                );
              }}
            />
          </div>
          <div
            className={classNames(styles.migrateTokens, {
              [styles.show]: hasAnIntegration === "yes",
            })}
          >
            <Radio
              data-testid="migrate-tokens"
              aria-hidden={hasAnIntegration === "no"}
              id="migrate-tokens"
              label={
                <span>
                  <span
                    className={classNames(styles.bold, styles.action, {
                      [styles.actionChecked]: migrateTokens,
                    })}
                  >
                    Help
                  </span>
                  {` me migrate existing tokens to Codat`}
                </span>
              }
              checked={migrateTokens === true}
              onClick={() =>
                sendEvent("Help With Migration Checkbox - Click", {
                  value: !migrateTokens,
                })
              }
              {...register("migrateTokens")}
            />
          </div>
          <footer className={styles.modalFooter}>
            <Button
              type="button"
              buttonName="Skip"
              variant="secondary"
              color="interface"
              onClick={onSkip}
              className={styles.secondaryBtn}
              id="skip-onboarding-button"
              data-testid="skip-button"
            >
              Skip
            </Button>
            <Button
              label="Continue setup"
              buttonName="Continue Setup"
              eventProperties={{
                hasBuiltAnIntegration: getValues().hasAnIntegration,
                tokenMigrationHelp:
                  getValues().hasAnIntegration === "yes"
                    ? getValues().migrateTokens
                    : undefined,
              }}
              className={styles.primaryBtn}
              type="submit"
              data-testid="continue-setup-button"
            />
          </footer>
        </form>
      </div>
    </ModalBody>
  );
};
